"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { type CircleActionVariant } from "./circle-action-variants";
import { LiquidPill, type LiquidPillSize } from "./liquid-pill";
import {
  type LiquidSurfaceBounceIntensity,
  type LiquidSurfaceShadow,
  type LiquidSurfaceStyle,
} from "./liquid-surface";
import { Z_INDEX } from "./z-index";

export type LiquidFixedCircleHorizontal = "left" | "center" | "right";
export type LiquidFixedCircleVertical = "top" | "center" | "bottom";

export interface LiquidFixedCircleButtonProps {
  /** Icono centrado dentro del botón circular. */
  icon: React.ReactNode;
  /** Variante cromática del LiquidPill. Default `slate`. */
  variant?: CircleActionVariant;
  /** Tamaño canónico del círculo (`xxs`–`2xl`). Default `md`. */
  size?: LiquidPillSize;
  /**
   * Anclaje horizontal en el viewport.
   * - `left` / `right`: distancia controlada por `offsetX`.
   * - `center`: centrado con `left: 50%` + `translateX(-50%)`.
   */
  horizontal?: LiquidFixedCircleHorizontal;
  /**
   * Anclaje vertical en el viewport.
   * - `top` / `bottom`: distancia controlada por `offsetY`.
   * - `center`: centrado con `top: 50%` + `translateY(-50%)`.
   */
  vertical?: LiquidFixedCircleVertical;
  /** Offset desde el borde horizontal (px o unidad CSS). Default `24`. */
  offsetX?: number | string;
  /** Offset desde el borde vertical (px o unidad CSS). Default `24`. */
  offsetY?: number | string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  /** Surface tinted permanente (estado activo). */
  active?: boolean;
  /**
   * Activa el material reactivo del `LiquidSurface` (backdrop + borde sensibles al
   * fondo). Default `false`.
   */
  reactive?: boolean;
  /** Rebote líquido en hover. Default `true`. */
  hoverBounce?: boolean;
  /** Deformación local en hover. Default `true`. */
  hoverDeform?: boolean;
  /** Intensidad del rebote/deformación. */
  bounceIntensity?: LiquidSurfaceBounceIntensity;
  /**
   * Elevación del LiquidSurface. Default `floating` (sombra expandida de proximidad).
   */
  shadow?: LiquidSurfaceShadow;
  /**
   * Variante visual del surface. Con `reactive` se fuerza `default` (glass)
   * salvo que se pase un valor explícito.
   */
  surfaceStyle?: LiquidSurfaceStyle;
  /**
   * Portal al DOM para escapar transforms de ancestros.
   * Default: `false` si `reactive` (backdrop-filter necesita el árbol de la página),
   * `true` si no es reactivo.
   */
  portal?: boolean;
  /** Id del contenedor portal. Con reactive default apunta a `#app-main-scroll`. */
  portalContainerId?: string;
  className?: string;
  "aria-label"?: string;
}

const DEFAULT_OFFSET = 24;
/**
 * Contenedores de portal (orden de preferencia con reactive):
 * - Sin portal (default si reactive): el FAB queda in-tree, backdrop-filter ve el contenido.
 * - `#app-main-scroll`: scroll principal de la app.
 * - `#site-container`: shell completo.
 */
const PORTAL_CONTAINER_APP_SCROLL = "app-main-scroll";
const PORTAL_CONTAINER_SITE = "site-container";

function resolvePortalContainer(
  containerId: string | undefined,
  reactive: boolean,
): HTMLElement | null {
  if (typeof document === "undefined") return null;

  if (containerId) {
    return document.getElementById(containerId) ?? document.body;
  }

  if (reactive) {
    return (
      document.getElementById(PORTAL_CONTAINER_APP_SCROLL) ??
      document.getElementById(PORTAL_CONTAINER_SITE) ??
      document.body
    );
  }

  return (
    document.getElementById(PORTAL_CONTAINER_SITE) ??
    document.body
  );
}

function toCssLength(value: number | string | undefined, fallback: number): string {
  if (value === undefined) return `${fallback}px`;
  return typeof value === "number" ? `${value}px` : value;
}

function buildFixedPositionStyle(
  horizontal: LiquidFixedCircleHorizontal,
  vertical: LiquidFixedCircleVertical,
  offsetX: number | string | undefined,
  offsetY: number | string | undefined,
): React.CSSProperties {
  const x = toCssLength(offsetX, DEFAULT_OFFSET);
  const y = toCssLength(offsetY, DEFAULT_OFFSET);
  const transforms: string[] = [];

  const style: React.CSSProperties = {
    position: "fixed",
    zIndex: Z_INDEX.FIXED_CIRCLE,
  };

  if (horizontal === "left") {
    style.left = x;
  } else if (horizontal === "right") {
    style.right = x;
  } else {
    style.left = "50%";
    transforms.push("translateX(-50%)");
  }

  if (vertical === "top") {
    style.top = y;
  } else if (vertical === "bottom") {
    style.bottom = y;
  } else {
    style.top = "50%";
    transforms.push("translateY(-50%)");
  }

  if (transforms.length > 0) {
    style.transform = transforms.join(" ");
  }

  return style;
}

/**
 * Botón circular flotante (`position: fixed`) compuesto sobre `LiquidPill`.
 *
 * Sigue el design language v2: delega 100% la superficie líquida al primitivo
 * `LiquidSurface` vía `LiquidPill` (`shape="circle"`). El wrapper fijo solo
 * resuelve posicionamiento en viewport y z-index canónico (`Z_INDEX.FIXED_CIRCLE`),
 * por debajo del navbar (`6000`) y por encima de dropdowns/contenido.
 *
 * Default: esquina inferior derecha con 24px de margen.
 */
export function LiquidFixedCircleButton({
  icon,
  variant = "slate",
  size = "md",
  horizontal = "right",
  vertical = "bottom",
  offsetX,
  offsetY,
  onClick,
  disabled = false,
  active = false,
  reactive = false,
  hoverBounce = true,
  hoverDeform = true,
  bounceIntensity,
  shadow = "floating",
  surfaceStyle,
  portal: portalProp,
  portalContainerId,
  className,
  "aria-label": ariaLabel,
}: LiquidFixedCircleButtonProps) {
  const [mounted, setMounted] = React.useState(false);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  /** Reactive + portal rompe backdrop-filter: por defecto render in-tree. */
  const portal = portalProp ?? !reactive;

  React.useEffect(() => {
    setMounted(true);
    if (portal) {
      setPortalContainer(resolvePortalContainer(portalContainerId, reactive));
    }
  }, [portal, portalContainerId, reactive]);

  const positionStyle = React.useMemo(
    () => buildFixedPositionStyle(horizontal, vertical, offsetX, offsetY),
    [horizontal, vertical, offsetX, offsetY],
  );

  const resolvedSurfaceStyle: LiquidSurfaceStyle =
    surfaceStyle ?? (reactive ? "default" : "sidebarInactive");

  const node = (
    <span
      className={cn("liquid-fixed-circle-button inline-flex", className)}
      style={positionStyle}
      data-horizontal={horizontal}
      data-vertical={vertical}
      data-variant={variant}
    >
      <LiquidPill
        as="button"
        type="button"
        shape="circle"
        size={size}
        variant={variant}
        icon={icon}
        active={active}
        disabled={disabled}
        onClick={onClick}
        surfaceStyle={resolvedSurfaceStyle}
        shadow={shadow}
        reactive={reactive}
        hoverBounce={hoverBounce}
        hoverDeform={hoverDeform}
        bounceIntensity={bounceIntensity}
        aria-label={ariaLabel}
      />
    </span>
  );

  if (portal && mounted && portalContainer) {
    return createPortal(node, portalContainer);
  }

  return node;
}
